import {
  faExclamationTriangle,
  faMagnifyingGlass,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MembersTable from './MembersTable';
import NominationsTable from './NominationsTable';
import PoolChips from './PoolChips';
import PoolDepthCard from './PoolDepthCard';
import PoolHealthChart from './PoolHealthChart';
import PoolTabs, { PoolTabValue } from './PoolTabs';
import AddWorkerModal from './modals/AddWorkerModal';
import CreatePoolModal from './modals/CreatePoolModal';
import RemoveMemberModal from './modals/RemoveMemberModal';
import ReviewNominationModal from './modals/ReviewNominationModal';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import Stack from '@/components/Stack';
import TextInput from '@/components/TextInput';
import { NATIVE_FEATURE_FLAG } from '@/constants/featureFlags';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import {
  PoolMemberRow,
  PoolNominationRow,
  PoolPagination,
  PoolRow,
  PreferredPoolsAPI,
  extractPoolsError,
} from '@/services/preferredPoolsAPI';
import styled from '@/styles';
import {
  trackPoolCardViewed,
  trackPoolNominationDecided,
  trackPoolThresholdChanged,
} from '@/util/vexo';

// Preferred Workers management (GRAVY-10265 F2/F10) — the shared feature
// block mounted on the client (Customer) profile and the job details page.
// Server-driven gate (P13): while PREFERRED_WORKER_POOLS_ENABLED is off the
// list endpoint 404s and this renders nothing at all. Tenant-admin only —
// both mounts also hide it for customer admins (P10).

const MEMBERS_PER_PAGE = 25;
const NOMINATIONS_PER_PAGE = 25;

const PaginationRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const PaginationInfo = styled('span', {
  fontSize: '12px',
  color: '#5B5B5B',
});

const PageButton = styled('button', {
  all: 'unset',
  cursor: 'pointer',
  fontSize: '14px',
  padding: '2px 8px',
  color: '#1C1C1C',
  '&:disabled': { color: '#DADADA', cursor: 'default' },
});

type GateState = 'loading' | 'hidden' | 'enabled';

const activePaginationTotalPages = (pagination: PoolPagination | null) =>
  pagination?.total_pages ?? 0;

type Props = {
  customerId: number | string;
  // GRAVY-10799: the client's market tax type, forwarded to the add-to-pool
  // worker search so candidates follow the same rule as staffing.
  jobTaxType?: string | null;
  initialSkillId?: number;
  // Job-page mount (F9): when the job's skill has no pool, show a create CTA
  // instead of silently falling back to another skill's pool. Ops can still
  // select any pool from the chart.
  strictSkill?: boolean;
};

const PreferredWorkers = ({
  customerId,
  jobTaxType = null,
  initialSkillId,
  strictSkill = false,
}: Props) => {
  // GRAVY-10802: the add-to-pool tier filter shares the Staff Shift modal's
  // gate (WorkerTable passes the same flag as showTier).
  const { variant: dependabilityV2Variant } = useFeatureFlag(
    NATIVE_FEATURE_FLAG.WorkerDependabilityV2,
  );
  const showTier = dependabilityV2Variant === 'on';
  const [gate, setGate] = useState<GateState>('loading');
  const [pools, setPools] = useState<PoolRow[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
  const [tab, setTab] = useState<PoolTabValue>('members');
  const [members, setMembers] = useState<PoolMemberRow[]>([]);
  const [membersPagination, setMembersPagination] =
    useState<PoolPagination | null>(null);
  const [membersPage, setMembersPage] = useState(1);
  const [nominations, setNominations] = useState<PoolNominationRow[]>([]);
  const [nominationsPagination, setNominationsPagination] =
    useState<PoolPagination | null>(null);
  const [nominationsPage, setNominationsPage] = useState(1);
  const [decidingId, setDecidingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [workerSearch, setWorkerSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PoolMemberRow | null>(null);
  const [reviewTarget, setReviewTarget] = useState<PoolNominationRow | null>(
    null,
  );

  const selectedPool = useMemo(
    () => pools.find((pool) => pool.id === selectedPoolId) ?? null,
    [pools, selectedPoolId],
  );
  const skillName = selectedPool?.skill.name ?? '';

  // pool_card_viewed fires once per customerId, on the first successful
  // listPools — not on every refreshAll()/loadPools() re-fetch that follows
  // a mutation (2026-09-02 analytics plan).
  const poolCardViewedFor = useRef<Set<string>>(new Set());
  // seconds_in_modal for pool_nomination_decided: stamped when the review
  // modal opens (setReviewTarget below), read when decide() resolves.
  const reviewOpenedAtRef = useRef<number | null>(null);

  const loadPools = useCallback(
    async (preferPoolId?: number) => {
      try {
        const result = await PreferredPoolsAPI.listPools(customerId);
        if (!result.enabled) {
          setGate('hidden');
          return;
        }
        setGate('enabled');
        setPools(result.pools);
        const customerKey = String(customerId);
        if (!poolCardViewedFor.current.has(customerKey)) {
          poolCardViewedFor.current.add(customerKey);
          trackPoolCardViewed({
            customer_id: customerId,
            enabled: true,
            pools_count: result.pools.length,
            pools_red: result.pools.filter((p) => p.health === 'red').length,
            pools_amber: result.pools.filter((p) => p.health === 'amber')
              .length,
            pools_green: result.pools.filter((p) => p.health === 'green')
              .length,
          });
        }
        setSelectedPoolId((current) => {
          const wanted = preferPoolId ?? current;
          if (wanted && result.pools.some((pool) => pool.id === wanted)) {
            return wanted;
          }
          const bySkill = initialSkillId
            ? result.pools.find((pool) => pool.skill.id === initialSkillId)
            : undefined;
          if (strictSkill) return bySkill?.id ?? null;
          return bySkill?.id ?? result.pools[0]?.id ?? null;
        });
      } catch (error) {
        console.error('[PreferredWorkers] Error:', error);
        // P13 means "404/403 -> the feature is off here, render nothing".
        // Hiding on ANY error made a transient 500 or a dropped connection
        // indistinguishable from that, so the whole card silently vanished
        // and ops had no way to tell "not enabled" from "broken". listPools
        // already maps the gate responses to enabled:false above, so reaching
        // this catch means something genuinely went wrong: keep the card and
        // say so.
        setGate('enabled');
        setActionError(
          extractPoolsError(error, 'Failed to load preferred worker pools'),
        );
      }
    },
    [customerId, initialSkillId, strictSkill],
  );

  useEffect(() => {
    loadPools();
  }, [loadPools]);

  // Rapid pool switches fire overlapping fetches whose resolution order is
  // not the click order — a slow response for the previous pool must not
  // paint over the current one (GRAVY-10337). Latest request wins.
  const membersLoadSeq = useRef(0);
  const nominationsLoadSeq = useRef(0);

  // A mutation on the last row of the last page leaves the current page
  // beyond total_pages; the server returns an empty slice for it, which
  // stranded the user on an empty list with no pager (GRAVY-10338). When a
  // page comes back empty but rows still exist, re-request the last valid
  // page. page <= lastPage on the retry, so it cannot recurse twice.
  const loadMembers = useCallback(async function load(
    poolId: number,
    page: number,
  ) {
    const seq = ++membersLoadSeq.current;
    try {
      const result = await PreferredPoolsAPI.listMembers(poolId, {
        page,
        per_page: MEMBERS_PER_PAGE,
      });
      if (seq !== membersLoadSeq.current) return;
      const lastPage = Math.max(1, result.pagination.total_pages);
      if (
        result.members.length === 0 &&
        result.pagination.total > 0 &&
        page > lastPage
      ) {
        setMembersPage(lastPage);
        load(poolId, lastPage);
        return;
      }
      setMembers(result.members);
      setMembersPagination(result.pagination);
    } catch (error) {
      if (seq !== membersLoadSeq.current) return;
      setActionError(extractPoolsError(error, 'Failed to load pool members'));
    }
  }, []);

  const loadNominations = useCallback(async function load(
    poolId: number,
    page: number,
  ) {
    const seq = ++nominationsLoadSeq.current;
    try {
      const result = await PreferredPoolsAPI.listNominations(poolId, {
        page,
        per_page: NOMINATIONS_PER_PAGE,
      });
      if (seq !== nominationsLoadSeq.current) return;
      const lastPage = Math.max(1, result.pagination.total_pages);
      if (
        result.nominations.length === 0 &&
        result.pagination.total > 0 &&
        page > lastPage
      ) {
        setNominationsPage(lastPage);
        load(poolId, lastPage);
        return;
      }
      setNominations(result.nominations);
      setNominationsPagination(result.pagination);
    } catch (error) {
      if (seq !== nominationsLoadSeq.current) return;
      setActionError(extractPoolsError(error, 'Failed to load nominations'));
    }
  }, []);

  useEffect(() => {
    if (!selectedPoolId) return;
    setMembers([]);
    setMembersPagination(null);
    setNominations([]);
    setNominationsPagination(null);
    setMembersPage(1);
    setNominationsPage(1);
    setWorkerSearch('');
    loadMembers(selectedPoolId, 1);
    loadNominations(selectedPoolId, 1);
  }, [selectedPoolId, loadMembers, loadNominations]);

  const refreshAll = useCallback(() => {
    if (!selectedPoolId) return;
    loadPools();
    loadMembers(selectedPoolId, membersPage);
    loadNominations(selectedPoolId, nominationsPage);
  }, [
    selectedPoolId,
    membersPage,
    nominationsPage,
    loadPools,
    loadMembers,
    loadNominations,
  ]);

  const handleSelectPool = (poolId: number) => {
    setActionError(null);
    setSelectedPoolId(poolId);
  };

  const handleThresholdChange = async (alertThreshold: number) => {
    if (!selectedPoolId) return;
    setActionError(null);
    // Capture BEFORE the await — selectedPool state could theoretically
    // shift under a slow request, and we want the value that was true when
    // this change was initiated, not whatever it drifted to by the time the
    // PATCH resolves.
    const previousThreshold = selectedPool?.alert_threshold ?? 0;
    try {
      await PreferredPoolsAPI.updatePool(selectedPoolId, {
        alert_threshold: alertThreshold,
      });
      // Fire only on a confirmed write — a failed PATCH must not report a
      // threshold change that never happened (2026-09-02 review finding).
      trackPoolThresholdChanged({
        pool_id: selectedPoolId,
        field: 'alert_threshold',
        from: previousThreshold,
        to: alertThreshold,
      });
      loadPools();
    } catch (error) {
      setActionError(
        extractPoolsError(error, 'Failed to update alert threshold'),
      );
    }
  };

  const decide = async (
    nomination: PoolNominationRow,
    action: 'approve' | 'decline',
  ) => {
    if (!selectedPoolId) return;
    setActionError(null);
    setDecidingId(nomination.nomination_id);
    try {
      if (action === 'approve') {
        await PreferredPoolsAPI.approveNomination(
          selectedPoolId,
          nomination.nomination_id,
        );
      } else {
        await PreferredPoolsAPI.declineNomination(
          selectedPoolId,
          nomination.nomination_id,
        );
      }
      trackPoolNominationDecided({
        nomination_id: nomination.nomination_id,
        decision: action === 'approve' ? 'approved' : 'declined',
        source: nomination.source,
        // Neither decide() call site (NominationsTable's inline row actions,
        // ReviewNominationModal's Approve/Decline) passes a note/reason
        // through today — always false until that lands.
        note_given: false,
        seconds_in_modal: reviewOpenedAtRef.current
          ? Math.round((Date.now() - reviewOpenedAtRef.current) / 1000)
          : null,
      });
      // Consume the stamp — an inline row decide() (no review modal opened)
      // that follows must not inherit a stale open time from a PREVIOUS
      // review.
      reviewOpenedAtRef.current = null;
      setReviewTarget(null);
      refreshAll();
    } catch (error) {
      setActionError(
        extractPoolsError(
          error,
          action === 'approve'
            ? 'Failed to approve nomination'
            : 'Failed to decline nomination',
        ),
      );
    } finally {
      setDecidingId(null);
    }
  };

  if (gate !== 'enabled') {
    return null;
  }

  const activePagination =
    tab === 'members' ? membersPagination : nominationsPagination;
  const activePage = tab === 'members' ? membersPage : nominationsPage;
  // Prototype .cd-pool-toolbar "Search GravyWorker": filters the loaded rows
  // by name, client-side — the members/nominations endpoints have no search
  // param, and realistic pools fit one 25-row page (target depth is 8).
  const searchTerm = workerSearch.trim().toLowerCase();
  const visibleMembers = searchTerm
    ? members.filter((member) =>
        (member.worker.name ?? '').toLowerCase().includes(searchTerm),
      )
    : members;
  const visibleNominations = searchTerm
    ? nominations.filter((nomination) =>
        (nomination.worker.name ?? '').toLowerCase().includes(searchTerm),
      )
    : nominations;
  const shownCount =
    tab === 'members' ? visibleMembers.length : visibleNominations.length;
  // Both tabs hide the pager the same way. The members tab used to show it
  // unconditionally, so an empty pool rendered "0 of 0  ‹ 1 / 0 ›" — a page
  // counter reading "page 1 of 0" next to an empty-state message.
  // Two separate decisions, which conflating them got wrong in both directions:
  //   - the COUNT ("3 of 3") is useful on a single page and should show
  //     whenever there are rows;
  //   - the PAGER (‹ 1 / 2 ›) is meaningless below two pages, and with
  //     total_pages 0 it rendered the nonsense "1 / 0" on an empty pool.
  const activeTotalPages = activePaginationTotalPages(
    tab === 'members' ? membersPagination : nominationsPagination,
  );
  // Server totals don't know about the client-side name filter, so the count
  // line would lie while a search is active — hide it instead.
  const showPagination = shownCount > 0 && !searchTerm;
  const showPageControls = activeTotalPages > 1;

  const changePage = (page: number) => {
    if (!selectedPoolId) return;
    if (tab === 'members') {
      setMembersPage(page);
      loadMembers(selectedPoolId, page);
    } else {
      setNominationsPage(page);
      loadNominations(selectedPoolId, page);
    }
  };

  return (
    <Card
      // Prototype .cd-outline-btn: a bordered button, not the Card actions'
      // plain text link (GRAVY-10370 item 1).
      actionsDropdown={
        <Button
          a11yLabel="Create new pool"
          appearance="outline"
          id="btn-create-pool"
          label="Create new pool"
          onClick={() => setCreateOpen(true)}
        />
      }
      title="Preferred workers"
    >
      <Card.Section>
        {/* Outside the pools.length branch: a failure to LOAD the list leaves
            pools empty, and nesting the alert in the non-empty branch meant
            that failure rendered as the cheerful "No preferred worker pools
            yet" empty state instead of the error. */}
        {actionError && (
          <Alert
            description={actionError}
            icon={faExclamationTriangle}
            status="danger"
            title="Something went wrong"
          />
        )}
        {pools.length === 0 ? (
          <EmptyState
            text="Create a pool to start tracking preferred workers for this client."
            title="No preferred worker pools yet"
          />
        ) : (
          <Stack vertical gap={24}>
            <PoolChips
              pools={pools}
              selectedPoolId={selectedPoolId}
              onSelect={handleSelectPool}
            />
            <PoolHealthChart
              pools={pools}
              selectedPoolId={selectedPoolId}
              onSelect={handleSelectPool}
            />
            {!selectedPool && strictSkill && (
              <EmptyState
                text="Create a pool for it to start collecting nominations."
                title="No pool for this job's skill yet"
              />
            )}
            {selectedPool && (
              <>
                <PoolDepthCard
                  pool={selectedPool}
                  onThresholdChange={handleThresholdChange}
                />
                {/* Prototype .cd-pool-toolbar: the search field sits inline
                    with "Add worker to pool" (GRAVY-10370 item 6). */}
                <Stack gap={16} style={{ width: '100%' }}>
                  <div
                    style={{
                      position: 'relative',
                      width: 400,
                      maxWidth: '100%',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      style={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 14,
                        height: 14,
                        color: '#979797',
                        zIndex: 3,
                        pointerEvents: 'none',
                      }}
                    />
                    <TextInput
                      css={{ paddingLeft: '34px', width: '100%' }}
                      id="pool-worker-search"
                      placeholder="Search GravyWorker"
                      value={workerSearch}
                      onChange={(event) => setWorkerSearch(event.target.value)}
                    />
                  </div>
                  <Stack justify="end">
                    <Button
                      a11yLabel="Add worker to pool"
                      appearance="plain"
                      iconLeft={faPlus}
                      label="Add worker to pool"
                      onClick={() => setAddOpen(true)}
                    />
                  </Stack>
                </Stack>
                <div style={{ width: '100%' }}>
                  <PoolTabs
                    pendingCount={selectedPool.pending_nominations_count}
                    tab={tab}
                    onTab={setTab}
                  />
                  {tab === 'members' ? (
                    <MembersTable
                      members={visibleMembers}
                      skillName={skillName}
                      onRemove={setRemoveTarget}
                    />
                  ) : (
                    <NominationsTable
                      decidingId={decidingId}
                      nominations={visibleNominations}
                      skillName={skillName}
                      onApprove={(nomination) => decide(nomination, 'approve')}
                      onDecline={(nomination) => decide(nomination, 'decline')}
                      onReview={(nomination) => {
                        setActionError(null);
                        reviewOpenedAtRef.current = Date.now();
                        setReviewTarget(nomination);
                      }}
                    />
                  )}
                </div>
                {showPagination && activePagination && (
                  <PaginationRow data-testid="poolPaginationRow">
                    <PaginationInfo data-testid="poolPaginationInfo">
                      {shownCount} of {activePagination.total}
                    </PaginationInfo>
                    {showPageControls && (
                      <Stack gap={8} justify="end" style={{ width: 'auto' }}>
                        <PageButton
                          aria-label="Previous page"
                          disabled={activePage <= 1}
                          type="button"
                          onClick={() => changePage(activePage - 1)}
                        >
                          ‹
                        </PageButton>
                        <PaginationInfo>
                          {activePagination.page} /{' '}
                          {activePagination.total_pages}
                        </PaginationInfo>
                        <PageButton
                          aria-label="Next page"
                          disabled={activePage >= activePagination.total_pages}
                          type="button"
                          onClick={() => changePage(activePage + 1)}
                        >
                          ›
                        </PageButton>
                      </Stack>
                    )}
                  </PaginationRow>
                )}
              </>
            )}
          </Stack>
        )}
      </Card.Section>
      {createOpen && (
        <CreatePoolModal
          customerId={customerId}
          existingSkillIds={pools
            .filter((pool) => pool.status === 'active')
            .map((pool) => pool.skill.id)}
          onClose={() => setCreateOpen(false)}
          onCreated={(pool) => loadPools(pool.id)}
          onSuccess={refreshAll}
        />
      )}
      {addOpen && selectedPool && (
        <AddWorkerModal
          customerId={String(customerId)}
          jobTaxType={jobTaxType}
          pool={selectedPool}
          showTier={showTier}
          onClose={() => setAddOpen(false)}
          onSuccess={refreshAll}
        />
      )}
      {removeTarget && selectedPoolId && (
        <RemoveMemberModal
          member={removeTarget}
          poolId={selectedPoolId}
          onClose={() => setRemoveTarget(null)}
          onSuccess={refreshAll}
        />
      )}
      {reviewTarget && (
        <ReviewNominationModal
          deciding={decidingId === reviewTarget.nomination_id}
          error={actionError}
          nomination={reviewTarget}
          skillName={skillName}
          onApprove={(nomination) => decide(nomination, 'approve')}
          onClose={() => {
            setActionError(null);
            setReviewTarget(null);
          }}
          onDecline={(nomination) => decide(nomination, 'decline')}
        />
      )}
    </Card>
  );
};

export default PreferredWorkers;
